<template>
  <v-container class="pa-0 area-container">
    <h2 v-if="title" class="pl-3 pr-0 mb-3">
      {{ title }}

      <v-tooltip bottom :max-width="maxWidth">
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            v-if="digest"
            size="16"
            class="mr-2"
            v-bind="attrs"
            v-on="on"
            @click="handleDigestClick(digest)"
          >
            {{ iconKey }}
          </v-icon>
        </template>
        <span v-if="tooltipOff && isSubscribed">{{ tooltipOff }}</span>
        <span v-if="tooltipOn && !isSubscribed">{{ tooltipOn }}</span>
      </v-tooltip>
    </h2>

    <div
      v-if="!slots || !slots.length"
      class="hyp-scroll pr-0"
      @scroll="handleScroll"
    >
      <slot></slot>
    </div>

    <v-select
      v-if="showPicker"
      :items="slots"
      :value="selectedSlot"
      v-model="selectedSlot"
      class=""
      :label="pickerLabel"
    ></v-select>

    <div v-if="slots && slots.length" class="pr-0" @scroll="handleScroll">
      <template v-for="slot in slots">
        <slot :name="slot.value" v-if="selectedSlot === slot.value"></slot>
      </template>
    </div>
  </v-container>
</template>

<script>
import {
  getDigests,
  enableDigest,
  disableDigest,
} from "@/services/digests/digests.service";
import { EVENT_ACTIONS, LATEST_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  SLOT_CHANGE: "slot-change",
};

export default {
  name: "LatestArea",
  props: {
    name: {
      type: String,
      default: "",
    },
    digest: {
      type: String,
      default: "",
    },
    tooltipOn: {
      type: String,
      default: "",
    },
    tooltipOff: {
      type: String,
      default: "",
    },
    title: {
      type: String,
    },
    titleHeight: {
      type: String,
      default: "1em",
    },
    bodyHeight: {
      type: String,
      default: "100%",
    },
    scrollX: {
      type: Boolean,
      default: false,
    },
    analyticsCategory: {
      type: String,
      required: false,
    },
    /**
     * An array of slot objects that correspond to the slots passed to this LatestArea. More than one slot will be displayed
     * at a time or if `showPicker` is true, the user can toggle between them.
     * @typedef {Object} slot
     * @property {string} text - The displayed title for this slot
     * @property {string} value - A unique id of the slot
     * @property {boolean} selected - If true, this slot is pre-selected
     */
    slots: {
      type: Array,
      default: null,
      required: false,
    },
    /**
     * If true, this area displays a picker for users to choose which slot of content they want to see in this area.
     */
    showPicker: {
      type: Boolean,
      default: false,
    },
    pickerLabel: {
      type: String,
      default: "Content type",
    },
    preselectedSlot: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      allDigests: this.digest.split(","),
      subscribedDigests: [],
      iconKey: "far fa-bell",
      isSubscribed: false,
      lastScroll: 0,
      selectedSlot: this.preselectedSlot,
    };
  },
  computed: {
    maxWidth() {
      return this.isSubscribed ? 200 : 400;
    },
  },
  async mounted() {
    await this.getSubscribedDigests();

    for (let d of this.allDigests) {
      if (this.subscribedDigests?.includes(d)) {
        this.iconKey = "fas fa-bell";
        this.isSubscribed = true;
      } else {
        this.iconKey = "far fa-bell";
        this.isSubscribed = false;
      }
    }
  },
  methods: {
    async handleDigestClick() {
      // toggle existing digests
      for (let d of this.allDigests) {
        if (this.subscribedDigests?.includes(d)) {
          await disableDigest({ type: d });
          this.iconKey = "far fa-bell";
          this.isSubscribed = false;
          this.handlePageTrackerEvent(
            LATEST_CATEGORIES.subscribe_digests,
            EVENT_ACTIONS.clicked,
            `Unsubscribe - ${d}`
          );
        } else {
          const res = await enableDigest({ type: d });

          if (res) {
            this.iconKey = "fas fa-bell";
            this.isSubscribed = true;
          }

          this.handlePageTrackerEvent(
            LATEST_CATEGORIES.subscribe_digests,
            EVENT_ACTIONS.clicked,
            `Subscribe - ${d}`
          );
        }
      }
      await this.getSubscribedDigests();
    },
    async getSubscribedDigests() {
      this.subscribedDigests = (await getDigests())?.digests;
    },
    handleScroll() {
      //Only track a new scroll event every minute so we don't send dozens of concurrent requests
      let isNewScrollEvent = new Date().valueOf() - this.lastScroll > 60000;

      if (isNewScrollEvent) {
        if (this.analyticsCategory) {
          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.scrolled,
            ""
          );
        }
        //If there are multiple component slots in this LatestArea, use the corresponding category for that slot
        else if (this.slots?.length) {
          const thisSlot = this.slots.find(
            (s) => s.value === this.selectedSlot,
            this
          );
          if (thisSlot && thisSlot.analyticsCategory) {
            this.handlePageTrackerEvent(
              thisSlot.analyticsCategory,
              EVENT_ACTIONS.scrolled,
              ""
            );
          }
        }
        //Reset the last scroll time
        this.lastScroll = new Date().valueOf();
      }
    },
  },
  watch: {
    selectedSlot: {
      handler(newValue) {
        this.$emit(EMITTED_ACTIONS.SLOT_CHANGE, newValue);
      },
    },
  },
};
</script>

<style scoped>
.area-container {
  height: 100%;
  max-height: 100%;
}
.hyp-scroll {
  overflow: hidden auto;
  height: 100%;
  max-height: 100%;
}
</style>
<style lang="scss">
.v-select__slot {
  border-bottom-color: transparent;
}
.v-select__slot .v-select__selection {
  font-size: large;
  font-weight: bold;
  &.theme--light {
    color: black;
  }
}
</style>
